/**
 * Proposal Templates Selector
 * Browse and apply reusable proposal templates
 */

import React, { useState, useEffect } from 'react';
import { FileText, Search, Loader2, Tag, Check, ChevronRight, X, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  return headers;
};

// Category badge colors
const CATEGORY_COLORS = {
  enterprise: 'bg-purple-100 text-purple-700',
  technical: 'bg-blue-100 text-blue-700',
  consulting: 'bg-amber-100 text-amber-700',
  general: 'bg-gray-100 text-gray-600',
};

// Template card
const TemplateCard = ({ template, onSelect, selected }) => {
  return (
    <div 
      className={`p-4 border rounded-lg cursor-pointer transition-all hover:border-blue-300 hover:shadow-sm ${
        selected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'bg-white'
      }`}
      onClick={() => onSelect(template)}
      data-testid={`template-card-${template.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          <h4 className="font-semibold text-gray-900">{template.name}</h4>
        </div>
        {selected && <Check className="w-5 h-5 text-blue-600" />}
      </div>
      
      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
        {template.description || 'No description'}
      </p>
      
      <div className="flex items-center justify-between">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_COLORS[template.category] || CATEGORY_COLORS.general}`}>
          {template.category}
        </span>
        <span className="text-xs text-gray-400">
          {template.sections?.length || 0} sections
        </span>
      </div>
      
      {template.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {template.tags.slice(0, 3).map((tag, idx) => (
            <span key={idx} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
              {tag}
            </span>
          ))}
          {template.tags.length > 3 && (
            <span className="text-xs text-gray-400">+{template.tags.length - 3}</span>
          )}
        </div>
      )}
      
      {template.is_public && (
        <div className="mt-2 flex items-center gap-1 text-xs text-emerald-600">
          <Sparkles className="w-3 h-3" />
          Built-in template
        </div>
      )}
    </div>
  );
};

// Placeholder replacement modal
const PlaceholderModal = ({ template, onApply, onClose }) => {
  const [replacements, setReplacements] = useState({});
  const [applying, setApplying] = useState(false);

  // Extract all unique placeholders from sections
  const allPlaceholders = new Set();
  template.sections?.forEach(section => {
    section.placeholders?.forEach(p => allPlaceholders.add(p));
  });
  const placeholderList = Array.from(allPlaceholders);

  const handleApply = async () => {
    setApplying(true);
    await onApply(replacements);
    setApplying(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Customize Template</h3>
            <p className="text-sm text-gray-500">{template.name}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-96">
          {placeholderList.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              No placeholders to customize. Template will be applied as-is.
            </p>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Fill in the values below to customize the template:
              </p>
              {placeholderList.map((placeholder) => {
                const label = placeholder.replace(/\{\{|\}\}/g, '').replace(/_/g, ' ');
                return (
                  <div key={placeholder}>
                    <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                      {label}
                    </label>
                    <input
                      type="text"
                      value={replacements[placeholder] || ''}
                      onChange={(e) => setReplacements(prev => ({
                        ...prev,
                        [placeholder]: e.target.value
                      }))}
                      placeholder={`Enter ${label.toLowerCase()}`}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid={`placeholder-${placeholder}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        <div className="p-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={applying}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            data-testid="apply-template-btn"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Apply Template
          </button>
        </div>
      </div>
    </div>
  );
};

// Main template selector
const ProposalTemplatesSelector = ({ 
  tenantId, 
  projectId, 
  onTemplateApplied,
  compact = false 
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showPlaceholders, setShowPlaceholders] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ tenant_id: tenantId });
        if (search) params.append('search', search);
        if (category !== 'all') params.append('category', category);

        const res = await axios.get(`${API_BASE}/proposal-templates?${params}`, {
          headers: getAuthHeaders()
        });
        setTemplates(res.data.templates || []);
      } catch (err) {
        console.error('Failed to load templates:', err);
        toast.error('Failed to load templates');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [tenantId, search, category]);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    
    // Check if template has placeholders
    const hasPlaceholders = template.sections?.some(s => s.placeholders?.length > 0);
    if (hasPlaceholders) {
      setShowPlaceholders(true);
    } else {
      handleApplyTemplate(template, {});
    }
  };

  const handleApplyTemplate = async (template, replacements) => {
    try {
      const params = new URLSearchParams({
        tenant_id: tenantId,
        project_id: projectId
      });

      const res = await axios.post(
        `${API_BASE}/proposal-templates/${template.id}/use?${params}`,
        replacements,
        { headers: getAuthHeaders() }
      );

      toast.success(`Template "${template.name}" applied`);
      setShowPlaceholders(false);
      
      if (onTemplateApplied) {
        onTemplateApplied(res.data);
      }
    } catch (err) {
      console.error('Failed to apply template:', err);
      toast.error('Failed to apply template');
    }
  };

  const categories = ['all', 'enterprise', 'technical', 'consulting', 'general'];

  return (
    <div className="bg-white border rounded-lg" data-testid="proposal-templates-selector">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-gray-900">Proposal Templates</h3>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates..."
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            data-testid="template-search"
          />
        </div>
        
        {/* Category filter */}
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-3 py-1 rounded-full text-sm whitespace-nowrap transition-colors ${
                category === cat
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Template list */}
      <div className={`p-4 ${compact ? 'max-h-80 overflow-y-auto' : ''}`}>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>No templates found</p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onSelect={handleSelectTemplate}
                selected={selectedTemplate?.id === template.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Placeholder modal */}
      {showPlaceholders && selectedTemplate && (
        <PlaceholderModal
          template={selectedTemplate}
          onApply={(replacements) => handleApplyTemplate(selectedTemplate, replacements)}
          onClose={() => setShowPlaceholders(false)}
        />
      )}
    </div>
  );
};

export default ProposalTemplatesSelector;
